import {$$} from '@vue-macros/reactivity-transform/macros'
import {IDatabasePropertyResponseInstance} from '@toolify/server/src/adapters/InstanceApiResponseAdapter/types/responseInstances/IDatabasePropertyResponseInstance'
import {IVotePropertyTypeInterfaces} from '@toolify/server/src/services/DatabasePropertyService/strategies/ColumnTypeStrategies/VotePropertyTypeStrategy/types/IVotePropertyTypeInterfaces'
import {IDatabaseItemResponseInstance} from '@toolify/server/src/controllers/DatabaseItemController/types/IDatabaseItemResponseInstance'
import {useAuthStore} from '@toolify/client/src/stores/AuthStore/useAuthStore'
import {PropertyStrategyComponentType} from '@toolify/client/src/modules/tableItems/enum/PropertyStrategyComponentType'
import {EntityType} from '@toolify/server/src/services/EntityService/enum/EntityType'
import {DatabaseViewType} from '@toolify/server/src/models/mongoose/DatabaseViewModel/enum/DatabaseViewType'
import {GroupSortModeType} from '@toolify/server/src/services/DatabasePropertyService/enum/GroupSortModeType'
import {COMMON_GROUP_SORT_LABEL_KEYS} from '@toolify/client/src/modules/databases/utils/groupSortModes'

export function useVoteValueTypeStrategy() {
  const authStore = useAuthStore()
  const strategyComponentType = PropertyStrategyComponentType.Dropdown

  const fullValueToText = (value: IVotePropertyTypeInterfaces['fullValue'], _property: IDatabasePropertyResponseInstance) => {
    if (!value) return '0'
    return String(value.count ?? 0)
  }

  const socketCellValueToClientCellValue = (
    previousValue: IDatabaseItemResponseInstance['cellValues'][string],
    newValue: IDatabaseItemResponseInstance['cellValues'][string],
    authorId: string,
  ) => {
    const previousFullValue = previousValue?.value as IVotePropertyTypeInterfaces['fullValue']
    const newSocketFullValue = newValue?.value as IVotePropertyTypeInterfaces['fullValue']

    let newFullValue = newSocketFullValue
    if (newSocketFullValue && previousFullValue) {
      const previousEntitiesById = new Map(
        (previousFullValue.latestEntities || []).map(entity => [`${entity.entityType}:${entity.entityId}`, entity]),
      )
      const mergedLatestEntities = (newSocketFullValue.latestEntities || []).map(entity => {
        if (entity.payload) {
          return entity
        }
        return previousEntitiesById.get(`${entity.entityType}:${entity.entityId}`) || entity
      })
      const hasCurrentUserVoted = authStore.currentEntity?.entityType === EntityType.User
        ? previousFullValue.hasCurrentUserVoted
        : newSocketFullValue.hasCurrentUserVoted
      newFullValue = {
        ...newSocketFullValue,
        latestEntities: mergedLatestEntities,
        hasCurrentUserVoted,
      }
    }

    return {
      ...previousValue,
      value: newFullValue,
    }
  }

  const canGroupByView = (viewType: DatabaseViewType, _property: IDatabasePropertyResponseInstance) =>
    viewType === DatabaseViewType.Table || viewType === DatabaseViewType.Timeline

  const sortGroupItems = (
    a: {fullValue: IVotePropertyTypeInterfaces['fullValue']},
    b: {fullValue: IVotePropertyTypeInterfaces['fullValue']},
  ): number => {
    const aCount = a.fullValue?.count ?? 0
    const bCount = b.fullValue?.count ?? 0
    return aCount - bCount
  }

  const compareItemValues = (
    valueA: IVotePropertyTypeInterfaces['fullValue'],
    valueB: IVotePropertyTypeInterfaces['fullValue'],
    sortDirection: '1' | '-1',
  ): number => {
    const countA = valueA?.count ?? 0
    const countB = valueB?.count ?? 0
    const comparison = countA - countB
    return sortDirection === '1' ? comparison : -comparison
  }

  const groupSort = {
    availableModes: [GroupSortModeType.ValueAsc, GroupSortModeType.ValueDesc, GroupSortModeType.CountDesc, GroupSortModeType.CountAsc],
    defaultMode: GroupSortModeType.ValueAsc,
    getLabelKey: (mode: GroupSortModeType): string => {
      if(mode === GroupSortModeType.ValueAsc) return 'database.header.groupSort.numericAsc'
      if(mode === GroupSortModeType.ValueDesc) return 'database.header.groupSort.numericDesc'
      return COMMON_GROUP_SORT_LABEL_KEYS[mode]
    },
  }

  return $$({
    fullValueToText,
    socketCellValueToClientCellValue,
    strategyComponentType,
    canGroupByView,
    sortGroupItems,
    compareItemValues,
    groupSort,
  })
}
